import bitrefillImage from "#/assets/store/bitrefill.png"
import brinkImage from "#/assets/store/brink.png"
import brinkFullImage from "#/assets/store/brink-full.png"
import ledgerNanoXImage from "#/assets/store/ledger-nano-x.png"
import ledgerStaxImage from "#/assets/store/ledger-stax.png"
import sheFiImage from "#/assets/store/shefi.png"
import sheFiFullImage from "#/assets/store/shefi-full.png"
import surveyImage from "#/assets/store/survey.png"

import links from "./links"

export type MarketItemData = {
  // This id will be displayed in market item detail page url.
  id: string
  type: "donation" | "store-product" | "gift-card" | "survey"
  name: string
  image: string
  fullImage: string
  thumbnailDescription: string
  thumbnailHighlightedText?: string
  mainTitle: string
  mainDescription: string
  successMessage: string
}

type MarketDonationItemData = MarketItemData & {
  // This id will be passed to donate method in Donations contract
  beneficiaryId: string
  details: string
  detailsAddress?: string
  recipient: string
  learnMoreLink: string
  learnMoreLinkLabel: string
  ctaButtonLabel: string
}

type MarketSurveyItemData = Pick<
  MarketDonationItemData,
  "id" | "type" | "name" | "mainTitle" | "image" | "thumbnailDescription"
>

type MarketStoreProductItemData = MarketItemData & {
  // This id will be passed to order method in Order contract
  chainProductId: number
  productDetailLink?: string
  productRedeemLink: string
}

type MarketGiftCardItemData = MarketItemData & {
  chainProductId: number[]
}

const SHEFI_DIRECT_MUSD_DONATION: MarketDonationItemData = {
  id: "shefi",
  beneficiaryId: "SheFi",
  type: "donation",
  name: "SheFi",
  image: sheFiImage,
  fullImage: sheFiFullImage,
  thumbnailDescription:
    "Support education and inclusion for women in Web3 and crypto",
  thumbnailHighlightedText: "From 1 MUSD",
  // Recipient should be the same as the one returned from contract
  recipient: "0x6e80164ea60673d64d5d6228beb684a1274bb017",
  mainTitle: "Contribute to SheFi",
  mainDescription:
    "SheFi is an 8-week program and global community helping women and nonbinary folks master crypto and Web3 through hands-on learning, peer collaboration, and practical experience.",
  details:
    "Your MUSD contributions will go to SheFi to provide women and nonbinary scholars the opportunity to join the course. Mezo will match up to $50K worth of contributions in MUSD.",
  successMessage:
    "Your MUSD contribution is confirmed. You’ve just helped open the door for more women to explore crypto, leadership, and hands-on learning.",
  learnMoreLink: links.SHEFI,
  learnMoreLinkLabel: "shefi.org",
  ctaButtonLabel: "Contribute with MUSD",
}

const BRINK_DIRECT_MUSD_DONATION: MarketDonationItemData = {
  id: "brink",
  beneficiaryId: "Brink",
  type: "donation",
  name: "Brink",
  image: brinkImage,
  fullImage: brinkFullImage,
  thumbnailDescription: "Support open source Bitcoin protocol developers",
  thumbnailHighlightedText: "From 1 MUSD",
  // Recipient should be the same as the one returned from contract
  recipient: "0x6e80164ea60673d64d5d6228beb684a1274bb017",
  mainTitle: "Donate to Brink",
  mainDescription:
    "Support open source Bitcoin development with a direct MUSD donation to Brink, a nonprofit that funds Bitcoin Core contributors and helps strengthen the network.",
  details:
    "Brink is entirely funded by donations from people and organizations who care about Bitcoin's future. They provide grants, mentorship, and education to developers around the world. Mezo will match up to $50K in MUSD donations.",
  successMessage:
    "Your MUSD contribution to Brink is confirmed. Thanks for supporting open source Bitcoin development.",
  learnMoreLink: links.BRINK,
  learnMoreLinkLabel: "brink.dev",
  ctaButtonLabel: "Donate with MUSD",
}

const LEDGER_NANO_X: MarketStoreProductItemData = {
  id: "ledger-nano-x",
  chainProductId: 1001,
  type: "store-product",
  name: "Ledger Nano X",
  image: ledgerNanoXImage,
  fullImage: ledgerNanoXImage,
  thumbnailDescription:
    "Secure, Bluetooth hardware wallet for owning and managing BitcoinFi assets",
  mainTitle: "Ledger Nano X - Mezo Edition",
  mainDescription:
    "Securely navigate through the world of BitcoinFi with your Mezo-branded Ledger Nano X. The Ledger Nano X is the most advanced Bluetooth-enabled hardware wallet, making sure you can securely manage your BTC, MUSD and other crypto assets with ownership and freedom.",
  productDetailLink: "https://shop.ledger.com/pages/ledger-nano-x",
  productRedeemLink: "https://shop.ledger.com/products/mezo-ledger-nano-x",
  successMessage:
    "Tap the button below to copy your code and reveal your Ledger.com redemption link.",
}

const LEDGER_STAX: MarketStoreProductItemData = {
  id: "ledger-stax",
  chainProductId: 1002,
  type: "store-product",
  name: "Ledger Stax",
  image: ledgerStaxImage,
  fullImage: ledgerStaxImage,
  thumbnailDescription:
    "Premium touchscreen wallet for secure, self-custody of your crypto",
  mainTitle: "Ledger Stax - Mezo Edition",
  mainDescription:
    "Navigate BitcoinFi with style and security using the Mezo-branded Ledger Stax. With its curved E Ink touchscreen, Bluetooth support, and secure chip technology, Stax is built for power users who value both usability and uncompromising self-custody. Full product details available on Ledger.com.",
  productDetailLink: "https://shop.ledger.com/pages/ledger-stax",
  productRedeemLink: "https://shop.ledger.com/products/mezo-ledger-stax",
  successMessage:
    "Tap the button below to copy your code and reveal your Ledger.com redemption link.",
}

const BITREFILL_GIFT_CARD: MarketGiftCardItemData = {
  id: "bitrefill-gift-card",
  type: "gift-card",
  name: "Bitrefill Gift Card",
  image: bitrefillImage,
  fullImage: bitrefillImage,
  thumbnailDescription:
    "Shop hundreds of top brands through Bitrefill using your MUSD",
  mainTitle: "Bitrefill Gift Cards",
  mainDescription: "",
  successMessage:
    "Use the button below to log in or create a Bitrefill account (only an email is required) and redeem your code to shop gift cards!",
  chainProductId: [1003, 1004, 1005, 1006],
}

export const MARKET_SURVEY_ITEM: MarketSurveyItemData = {
  id: "survey",
  type: "survey",
  name: "Survey",
  mainTitle: "Help shape the future",
  image: surveyImage,
  thumbnailDescription:
    "Tell us how you’d spend your mUSD. Take our quick survey and share your wishlist",
}

export const MARKET_DONATION_ITEMS: MarketDonationItemData[] = [
  BRINK_DIRECT_MUSD_DONATION,
  SHEFI_DIRECT_MUSD_DONATION,
]

export const MARKET_STORE_PRODUCT_ITEMS: MarketStoreProductItemData[] = [
  LEDGER_NANO_X,
  LEDGER_STAX,
]

export const MARKET_GIFT_CARD_ITEMS: MarketGiftCardItemData[] = [
  BITREFILL_GIFT_CARD,
]

export const MARKET_ITEMS: (
  | MarketDonationItemData
  | MarketStoreProductItemData
  | MarketGiftCardItemData
  | MarketSurveyItemData
)[] = [
  ...MARKET_STORE_PRODUCT_ITEMS,
  ...MARKET_GIFT_CARD_ITEMS,
  ...MARKET_DONATION_ITEMS,
  MARKET_SURVEY_ITEM,
]
